import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Fuel,
  Car,
  MapPin,
  Database,
  Lock,
  Share2,
  Clock,
  UserCheck,
  Baby,
  RefreshCcw,
  Mail,
  ArrowLeft,
} from "lucide-react";

const EFFECTIVE_DATE = "25 August 2026";
const CONTACT_EMAIL = "privacy@rayfuel.app";
const COMPANY = "Raybotix Digital";

const SECTIONS = [
  { id: "who-we-are",      label: "Who we are" },
  { id: "data-we-collect", label: "Data we collect" },
  { id: "location",        label: "Location permissions" },
  { id: "how-we-use",      label: "How we use your data" },
  { id: "storage",         label: "Storage & protection" },
  { id: "sharing",         label: "Sharing & third parties" },
  { id: "retention",       label: "Retention" },
  { id: "your-rights",     label: "Your rights" },
  { id: "children",        label: "Children" },
  { id: "changes",         label: "Changes to this policy" },
  { id: "contact",         label: "Contact us" },
];

export default function Privacy() {
  return (
    <div
      className="min-h-screen bg-[color:var(--bg-base,#0A0A0A)] text-[color:var(--text-primary,#FFFFFF)] grain"
      data-testid="privacy-page"
    >
      {/* Header strip */}
      <header className="border-b border-[color:var(--border-line,#262626)] sticky top-0 z-30 backdrop-blur-xl bg-[color:var(--bg-base,#0A0A0A)]/85">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="privacy-home-link"
          >
            <div
              className="w-9 h-9 flex items-center justify-center"
              style={{ background: "var(--accent, #FF5A1F)", color: "var(--accent-fg, #000)" }}
            >
              <Fuel size={20} strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-lg font-black tracking-tighter">RAYFUEL</span>
              <span className="ft-overline text-[9px]">Command · Analyse · Optimise</span>
            </div>
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-[color:var(--text-secondary,#A3A3A3)] hover:text-white transition-colors"
            data-testid="privacy-back-link"
          >
            <ArrowLeft size={16} /> Back to app
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-bold tracking-wide uppercase mb-5">
            <ShieldCheck size={14} /> Privacy Policy
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-4">
            Your fuel data,<br />on your terms.
          </h1>
          <p className="text-[color:var(--text-secondary,#A3A3A3)] max-w-2xl text-base sm:text-lg">
            RayFuel is a personal &amp; fleet vehicle logging platform.
            This policy explains what we collect, why we collect it, and the
            controls you have. Written for real humans — no dark patterns.
          </p>
          <p className="mt-4 text-xs font-mono text-[color:var(--text-tertiary,#737373)]" data-testid="privacy-effective-date">
            Effective date: {EFFECTIVE_DATE}
          </p>
        </div>

        {/* Two-column layout: TOC + Content */}
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 lg:gap-12">
          {/* Sticky TOC (desktop) */}
          <nav
            className="hidden md:block sticky top-24 self-start border-l border-[color:var(--border-line,#262626)] pl-4"
            aria-label="Table of contents"
            data-testid="privacy-toc"
          >
            <p className="ft-overline mb-3">Contents</p>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-[color:var(--text-secondary,#A3A3A3)] hover:text-[#FF5A1F] transition-colors"
                    data-testid={`privacy-toc-${s.id}`}
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <article className="prose-invert max-w-none space-y-10">
            <Section id="who-we-are" icon={ShieldCheck} title="1. Who we are">
              <p>
                RayFuel is a product of <strong>{COMPANY}</strong>. It helps you
                log refuels, EV charging sessions, routine service, repairs and
                bills for your personal or fleet vehicles, and turns that into
                mileage, cost-per-kilometre and wear insights.
              </p>
              <p>
                We are the data controller for the personal information covered
                by this policy. If you are a member invited into an account by
                an admin, your account admin controls the fleet-level records
                and we process them on their behalf.
              </p>
            </Section>

            <Section id="data-we-collect" icon={Database} title="2. Data we collect">
              <p>We only collect what the app actually needs to do its job.</p>

              <SubBlock
                icon={UserCheck}
                title="Account details"
                bullets={[
                  "Your name and email address (used to sign you in and send transactional emails).",
                  "A hashed password (we never store the plain-text password — we use industry-standard bcrypt).",
                  "Optional profile fields: preferred city, appearance preferences, role, and phone number if you add it.",
                ]}
              />

              <SubBlock
                icon={Car}
                title="Vehicle information"
                bullets={[
                  "Vehicle number / registration, name, make/model (if you enter it), fuel type, powertrain, initial odometer.",
                  "Vehicle documents you choose to upload (RC, Insurance, PUC) — kept only so we can remind you before they expire.",
                ]}
              />

              <SubBlock
                icon={Fuel}
                title="Fuel &amp; charging logs"
                bullets={[
                  "Refuel entries: date, odometer, litres, rate, amount, station, fuel type, notes.",
                  "EV charging sessions: date, kWh, cost, home/public location type, provider name, charger type, duration.",
                  "Service &amp; repair records: workshop, parts, labour, category, components, uploaded bill photos and OCR-extracted text.",
                ]}
              />

              <SubBlock
                icon={MapPin}
                title="Device &amp; usage information"
                bullets={[
                  "Basic device metadata your browser or app sends automatically (user-agent, screen size, timezone).",
                  "Log data around API errors so we can debug and improve the product. This does not include personal message content.",
                  "If you enable web push notifications, an anonymous subscription token from your browser.",
                ]}
              />

              <p className="text-sm text-[color:var(--text-muted,#A3A3A3)]">
                We do <strong>not</strong> collect: precise real-time location,
                contacts, SMS/call logs, photos other than what you explicitly
                upload, or advertising identifiers. RayFuel does not run
                third-party advertising SDKs.
              </p>
            </Section>

            <Section id="location" icon={MapPin} title="3. Location permissions">
              <p>
                RayFuel uses your <strong>city preference</strong> — not your
                precise GPS — to fetch fuel and EV charging rates that apply to
                where you drive. You choose your city from a list; you can
                change it any time from the header selector.
              </p>
              <p>
                We do <strong>not</strong> track your live location.
                We do not enable Android's <code>ACCESS_FINE_LOCATION</code> or
                <code>ACCESS_COARSE_LOCATION</code> permissions in the app.
                If a future release ever needs location for a specific feature
                (e.g. auto-detecting the nearest petrol pump), we will ask you
                first and explain exactly what the location is used for.
              </p>
              <p className="text-sm text-[color:var(--text-muted,#A3A3A3)]">
                Uploaded receipt photos may contain their own EXIF location
                metadata. We strip GPS EXIF from images before storing them.
              </p>
            </Section>

            <Section id="how-we-use" icon={Fuel} title="4. How we use your data">
              <ul>
                <li>
                  <strong>Deliver the product</strong>: sign you in, remember
                  your vehicles, calculate mileage / cost-per-km / TCO, run the
                  Ask&nbsp;RayFuel AI assistant grounded on your records.
                </li>
                <li>
                  <strong>Improve the product</strong>: understand which
                  screens are used, catch and fix bugs, tune AI insights.
                </li>
                <li>
                  <strong>Communicate</strong>: send transactional emails
                  (welcome, password reset, receipt uploaded), and — only if
                  you opt in during signup — product update newsletters.
                </li>
                <li>
                  <strong>Keep the service safe</strong>: rate limiting,
                  brute-force protection, audit logs of admin actions.
                </li>
              </ul>
              <p className="text-sm text-[color:var(--text-muted,#A3A3A3)]">
                We do not sell your data. We do not use your data to train
                foundation models for other customers.
              </p>
            </Section>

            <Section id="storage" icon={Lock} title="5. How we store &amp; protect your data">
              <ul>
                <li>
                  <strong>Encryption in transit</strong>: every request between
                  the app, the mobile client and our servers goes over TLS
                  (HTTPS). We do not accept unencrypted traffic.
                </li>
                <li>
                  <strong>Encryption at rest</strong>: databases and object
                  storage buckets holding your records and uploaded receipts
                  are encrypted at rest using AES-256.
                </li>
                <li>
                  <strong>Password hashing</strong>: passwords are salted and
                  hashed with bcrypt. Even our engineers cannot read them.
                </li>
                <li>
                  <strong>Multi-tenant isolation</strong>: every record is
                  scoped to an <code>account_id</code>. Users of one garage
                  cannot see or query records that belong to another garage —
                  enforced at every API boundary.
                </li>
                <li>
                  <strong>Least-privilege access</strong>: only a small,
                  audited team of engineers can access production data, and
                  only when responding to a customer incident.
                </li>
                <li>
                  <strong>Backups</strong>: we take daily encrypted database
                  snapshots kept for 30 days for disaster recovery.
                </li>
              </ul>
            </Section>

            <Section id="sharing" icon={Share2} title="6. Sharing &amp; third parties">
              <p>
                RayFuel relies on a small set of reputable processors to
                function. Data shared with them is limited to what they
                strictly need.
              </p>
              <ul>
                <li>
                  <strong>Cloud hosting</strong> — runs our servers and
                  database. No access to plain-text passwords.
                </li>
                <li>
                  <strong>Stripe</strong> — payment processing when you
                  upgrade to a paid plan. Card details go directly to Stripe;
                  we only see the last 4 digits, brand, and expiry.
                </li>
                <li>
                  <strong>AI providers (OpenAI, Anthropic, Google)</strong> —
                  power Ask RayFuel, AI insights and receipt OCR. We send only
                  the specific records needed to answer your question, and
                  configure the vendor to <em>not</em> retain that data for
                  training.
                </li>
                <li>
                  <strong>Push notification services</strong> — deliver
                  reminders (e.g. RC expiry, service due). Only the encrypted
                  push token and the notification body are shared.
                </li>
                <li>
                  <strong>Fuel price data source (goodreturns.in)</strong> —
                  we read publicly available city fuel rates. No personal data
                  is shared with them.
                </li>
              </ul>
              <p className="text-sm text-[color:var(--text-muted,#A3A3A3)]">
                We disclose data to law enforcement only in response to a
                valid, legally binding request that identifies specific users
                or records.
              </p>
            </Section>

            <Section id="retention" icon={Clock} title="7. How long we keep your data">
              <ul>
                <li>
                  <strong>Active records</strong>: kept for as long as your
                  account is active, so your history stays intact.
                </li>
                <li>
                  <strong>Deleted account</strong>: personal identifiers are
                  removed within 30 days of deletion. Aggregated,
                  de-identified analytics may be retained.
                </li>
                <li>
                  <strong>Backups</strong>: rolling encrypted snapshots for up
                  to 30 days.
                </li>
                <li>
                  <strong>Legal / accounting records</strong>: kept as long as
                  required by applicable tax and commercial laws.
                </li>
              </ul>
            </Section>

            <Section id="your-rights" icon={UserCheck} title="8. Your rights">
              <p>Regardless of where you live, you can:</p>
              <ul>
                <li>
                  <strong>Access</strong> — export every vehicle, refuel,
                  charging, service and bill record from the Timeline export.
                </li>
                <li><strong>Correct</strong> — edit any record you have permission to modify.</li>
                <li><strong>Delete</strong> — remove a record, a vehicle, or your entire account.</li>
                <li>
                  <strong>Object / opt out</strong> — turn off marketing emails
                  and push notifications from Settings → Notifications.
                </li>
                <li>
                  <strong>Complain</strong> to your local data-protection
                  authority if you believe we've mishandled your data.
                </li>
              </ul>
              <p>
                To exercise any of these, write to us at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF5A1F] hover:underline">
                  {CONTACT_EMAIL}
                </a>. We respond within 30 days.
              </p>
            </Section>

            <Section id="children" icon={Baby} title="9. Children">
              <p>
                RayFuel is intended for adults who drive or manage vehicles.
                We do not knowingly collect data from anyone under 16. If we
                learn we have accidentally collected data from a minor, we
                delete it.
              </p>
            </Section>

            <Section id="changes" icon={RefreshCcw} title="10. Changes to this policy">
              <p>
                If we make a material change to how we handle your data, we'll
                update the effective date at the top and notify active users
                via email or an in-app banner before the change takes effect.
              </p>
            </Section>

            <Section id="contact" icon={Mail} title="11. Contact us">
              <p>
                Questions, requests or feedback about privacy:{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF5A1F] hover:underline">
                  {CONTACT_EMAIL}
                </a>
                . We read every message.
              </p>
              <p className="text-sm text-[color:var(--text-muted,#A3A3A3)]">
                {COMPANY} · Ahmedabad, Gujarat, India
              </p>
            </Section>

            <div className="pt-6 border-t border-[color:var(--border-line,#262626)] text-xs text-[color:var(--text-tertiary,#737373)] font-mono">
              RAYFUEL · Privacy v1 · {EFFECTIVE_DATE}
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} data-testid={`privacy-section-${id}`} className="scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-9 h-9 flex items-center justify-center rounded-lg"
          style={{ background: "var(--accent, #FF5A1F)", color: "var(--accent-fg, #000)" }}
        >
          <Icon size={18} strokeWidth={2.4} />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
          {title}
        </h2>
      </div>
      <div className="space-y-3 text-[color:var(--text-secondary,#D1D1D1)] leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_code]:font-mono [&_code]:text-xs [&_code]:bg-[color:var(--bg-elevated,#141414)] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded">
        {children}
      </div>
    </section>
  );
}

function SubBlock({ icon: Icon, title, bullets }) {
  return (
    <div className="border-l-2 border-[#FF5A1F]/40 pl-4 py-1 my-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[#FF5A1F]" />
        <h3 className="font-display text-base font-bold tracking-tight text-white">{title}</h3>
      </div>
      <ul className="text-sm">
        {bullets.map((b, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: b }} />
        ))}
      </ul>
    </div>
  );
}
